// The car form's data rules, kept apart from the form itself.
//
// Nothing here imports React. That is deliberate: these are the parts worth
// testing, and importing the modal would drag in the shared component barrel
// and its router imports, which the CRA jest setup cannot resolve. Pure data
// in, pure data out — so the rules can be tested without a DOM.

const today = () => new Date().toISOString().slice(0, 10);

export const emptyCar = () => ({
  brand: '',
  model: '',
  trim: '',
  variant: 'pmt',
  year: '',
  registration_number: '',
  engine_number: '',
  chassis_number: '',
  color: '',
  km_driven: '',
  owners_count: '',
  rc_status: 'with_rc',
  insurance_valid_till: '',
  ownership: 'own',
  owner_name: '',
  owner_phone: '',
  purchase_price: '',
  asking_price: '',
  owner_price: '',
  commission_type: 'fixed',
  commission_value: '',
  acquired_on: today(),
  description: '',
  notes: '',
});

// Fields the model stores as NULL when empty. Everything else that can be
// emptied is a CharField/TextField with default='' — sending null to one of
// those fails validation, so the two groups have to be told apart.
const CLEARS_TO_NULL = new Set([
  'km_driven', 'owners_count', 'insurance_valid_till',
  'purchase_price', 'asking_price', 'owner_price', 'commission_value',
]);

// Required, or always carrying a value from a <Select>. A blank in one of
// these means "I didn't touch it", never "wipe it" — sending '' would be
// rejected, and on a correction that reads as the whole edit failing.
const NEVER_CLEARED = new Set([
  'brand', 'model', 'acquired_on', 'ownership', 'variant', 'rc_status', 'commission_type',
]);

/** Car payload (from CarSerializer) → form values. Inputs are controlled, so
 *  every null has to become '' or React switches them to uncontrolled. */
export const formFromCar = (car) => {
  const seeded = {};
  for (const key of Object.keys(emptyCar())) {
    const v = car?.[key];
    seeded[key] = v === null || v === undefined ? '' : String(v);
  }
  return seeded;
};

/** Form values → request body.
 *
 *  Adding and correcting need OPPOSITE treatment of a blank box, which is why
 *  this is one function with a flag rather than two nearly-identical ones:
 *
 *  - adding     → drop it. '' is not a missing number and the backend says so.
 *  - correcting → send it. Otherwise a wrong value can be changed but never
 *    removed, and "clear the box and save" would silently do nothing.
 */
export const buildCarPayload = (form, editing) => {
  if (!editing) {
    return Object.fromEntries(
      Object.entries(form).filter(([, v]) => v !== '' && v !== null),
    );
  }
  const payload = {};
  for (const [k, v] of Object.entries(form)) {
    const blank = v === '' || v === null || v === undefined;
    if (!blank) { payload[k] = v; continue; }
    if (NEVER_CLEARED.has(k)) continue;
    payload[k] = CLEARS_TO_NULL.has(k) ? null : '';
  }
  return payload;
};
